import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import StructuredData from '@/components/base/StructuredData';
import { categories, featuredContents, detailContents } from '@/mocks/content';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const contentId = parseInt(id || '1', 10);
  const detail = detailContents[contentId];
  const item = detail || featuredContents.find((c) => c.id === contentId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">内容不存在</h2>
          <p className="text-sm text-foreground-600 mb-6">找不到该影视内容，可能已下架</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line mr-2"></i>返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryLabel = categories.find((c) => c.slug === item.category)?.name || '';
  const relatedItems = detail?.relatedContent
    ? detail.relatedContent.map((rid) => featuredContents.find((c) => c.id === rid)).filter(Boolean)
    : [];

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dramaslab.github.io/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://dramaslab.github.io/category/${item.category}` },
      { '@type': 'ListItem', position: 3, name: item.title, item: `https://dramaslab.github.io/article/${item.id}` },
    ],
  };

  const articleLD = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${item.title} - yy6080在线观看`,
    description: item.description,
    datePublished: `${item.year}-01-01`,
    author: { '@type': 'Organization', name: 'yy6080' },
    publisher: { '@type': 'Organization', name: 'yy6080' },
  };

  const seoTitle = `${item.title} - yy6080免费在线观看 | yy6080${categoryLabel}`;
  const seoDesc = `${item.title}，${item.type}，导演${item.director}，主演${item.actors.join('、')}。${item.description} 尽在yy6080免费高清在线观看。`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`yy6080,yy6080在线观看,${item.title},yy6080${categoryLabel},yy6080高清视频`}
        canonicalUrl={`https://dramaslab.github.io/article/${item.id}`}
        ogType="article"
      />
      <StructuredData data={breadcrumbLD} />
      <StructuredData data={articleLD} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: categoryLabel, path: `/category/${item.category}` },
              { label: item.title },
            ]}
          />

          <article className="px-4 md:px-6 py-6 w-full">
            <div className="max-w-5xl mx-auto">
              {/* Detail Header */}
              <div className="flex flex-col lg:flex-row gap-8 mb-12">
                <div className="flex-shrink-0 w-full sm:w-[300px] mx-auto lg:mx-0">
                  <div className="rounded-xl overflow-hidden aspect-[3/4]">
                    <img
                      src={item.image}
                      alt={`${item.title}海报 - yy6080免费在线观看`}
                      className="w-full h-full object-cover object-top"
                      title={`${item.title} - yy6080在线观看`}
                    />
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-center">
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                    {item.title}
                  </h1>
                  <div className="flex items-center gap-4 mb-4 flex-wrap">
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {categoryLabel}
                    </span>
                    <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {item.type}
                    </span>
                    <div className="flex items-center gap-1 text-sm text-foreground-600">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="font-semibold">{item.rating}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm mb-6">
                    <InfoRow label="导演" value={item.director} />
                    <InfoRow label="年份" value={String(item.year)} />
                    <InfoRow label="地区" value={item.region} />
                    <InfoRow label="集数/版本" value={item.episodeInfo} />
                    <InfoRow label="更新时间" value={item.updateInfo} />
                    <InfoRow label="主演" value={item.actors.join(' / ')} />
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-fill text-lg"></i>
                      立即播放
                    </button>
                    <button className="flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              {/* Description */}
              <section className="mb-12">
                <h2 className="text-lg font-bold font-heading text-foreground-900 mb-4">剧情简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                  {(detail as typeof detailContents[number])?.fullDescription || item.description}
                </p>
              </section>

              {/* Tags */}
              <div className="flex items-center gap-2 flex-wrap mb-12">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Related Content */}
              {relatedItems.length > 0 && (
                <section className="border-t border-background-200/70 pt-10">
                  <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                    {relatedItems.map((related) =>
                      related ? (
                        <Link
                          key={related.id}
                          to={`/article/${related.id}`}
                          className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                        >
                          <div className="relative aspect-[3/4] overflow-hidden">
                            <img
                              src={related.image}
                              alt={`${related.title} - yy6080在线观看`}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              title={`${related.title} - yy6080推荐`}
                            />
                            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                              {related.rating}
                            </div>
                          </div>
                          <div className="p-4">
                            <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 whitespace-nowrap">
                              {related.title}
                            </h3>
                            <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                              {related.year} · {categories.find((c) => c.slug === related.category)?.name}
                            </p>
                          </div>
                        </Link>
                      ) : null
                    )}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-foreground-500 whitespace-nowrap">{label}：</span>
      <span className="text-foreground-800 truncate">{value}</span>
    </div>
  );
}